import { Show } from "shared/components";
import { formatPhone } from "shared/utils";
import { LinkifiedText } from "shared/utils/linkify-text";
import { IconAIApplicationSupportAgent } from "shared/components/ai/IconAI/icons/IconAIApplicationSupportAgent";
import { MessageContentCard } from "./MessageContentCard";
import { MessageAttachments } from "./MessageAttachments";
import { MessageTranslateButton } from "./MessageTranslateButton";
import type { MessageTranslation } from "../hooks/useMessageTranslation";
import type { Attachment } from "../types";

interface ChatContentProps {
  isFromContact: boolean;
  body: string;
  aiFlagged?: boolean;
  sentAsSms?: boolean;
  sentAsSmsTo?: string | null;
  smsFailedReason?: string | null;
  receivedViaSms?: boolean;
  attachments?: Attachment[];
  className?: string;
  translation: MessageTranslation;
  translateInBubble: boolean;
}

const smsFailedLabel = (reason: string): string =>
  reason === "no_number_configured"
    ? "Not also sent as SMS — no phone number is set up to send from"
    : "Not also sent as SMS";

// Native chat messages are plain text — no from/to header or dividers.
export const ChatContent = ({
  isFromContact,
  body,
  aiFlagged,
  sentAsSms,
  sentAsSmsTo,
  smsFailedReason,
  receivedViaSms,
  attachments,
  className,
  translation,
  translateInBubble
}: ChatContentProps) => {
  const displayBody =
    translation.showingTranslation && translation.translatedText !== null
      ? translation.translatedText
      : body;

  return (
    <MessageContentCard isFromContact={isFromContact} className={className}>
      <Show if={Boolean(body)}>
        <div className="text-sm text-content-1000 [overflow-wrap:anywhere] whitespace-pre-wrap">
          <LinkifiedText text={displayBody} showFullUrl />
        </div>
      </Show>
      <MessageAttachments attachments={attachments} />
      <Show if={Boolean(receivedViaSms) && isFromContact}>
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-alpha-40 pt-3 text-xs font-medium text-content-300">
          <span>via SMS</span>
          <Show if={translateInBubble}>
            <MessageTranslateButton translation={translation} />
          </Show>
        </div>
      </Show>
      <Show if={Boolean(sentAsSms) && !isFromContact}>
        <div className="mt-3 border-t border-alpha-40 pt-3 text-xs font-medium text-content-300">
          {sentAsSmsTo
            ? `Also sent as SMS to ${formatPhone(sentAsSmsTo)}`
            : "Also sent as SMS"}
        </div>
      </Show>
      <Show if={Boolean(smsFailedReason) && !sentAsSms && !isFromContact}>
        <div className="mt-3 border-t border-alpha-40 pt-3 text-xs font-medium text-warning-600">
          {smsFailedLabel(smsFailedReason ?? "")}
        </div>
      </Show>
      <Show if={Boolean(aiFlagged)}>
        <div className="mt-3 flex items-center gap-2 border-t border-alpha-40 pt-3">
          <IconAIApplicationSupportAgent bgSize={20} />
          <span className="text-sm font-medium text-content-1000">
            Flagged by Application support agent
          </span>
        </div>
      </Show>
    </MessageContentCard>
  );
};
